import { IconButton, TrashIcon } from '@allrouted/ui/Icons';

/**
 * Custom fields on an order, as name and value rows.
 *
 * Integrations send these (a visit id, a model number, a warranty flag); a
 * dispatcher could only read them. Edited as a list rather than a fixed form
 * because each operation names its own fields, and saved as a whole: the set
 * on screen is the set the order keeps.
 */

export interface FieldRow {
  key: string;
  value: string;
}

export function toRows(values: unknown): FieldRow[] {
  if (!values || typeof values !== 'object') return [];
  return Object.entries(values as Record<string, unknown>).map(([key, value]) => ({
    key,
    value: value === null || value === undefined ? '' : String(value),
  }));
}

/** The rows as the API takes them, or why they cannot be saved. */
export function toFields(rows: FieldRow[]): { ok: true; fields: Record<string, string> } | { ok: false; problem: string } {
  const fields: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key && !row.value.trim()) continue; /* an untouched new row */
    if (!key) return { ok: false, problem: 'Every custom field needs a name.' };
    if (key in fields) return { ok: false, problem: `“${key}” appears twice. Each name can be used once.` };
    fields[key] = row.value.trim();
  }
  return { ok: true, fields };
}

export function CustomFieldsEditor({ rows, onChange }: { rows: FieldRow[]; onChange: (rows: FieldRow[]) => void }) {
  const names = rows.map((r) => r.key.trim()).filter(Boolean);
  const dupes = new Set(names.filter((n, i) => names.indexOf(n) !== i));
  const set = (i: number, patch: Partial<FieldRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <div className="cf-editor">
      {rows.length === 0 ? <p className="cf-empty">No custom fields yet.</p> : null}
      {rows.map((row, i) => (
        <div className="cf-row" key={i} data-dupe={dupes.has(row.key.trim()) ? 'true' : undefined}>
          <input
            className="field"
            value={row.key}
            placeholder="Name, e.g. Visit ID"
            maxLength={100}
            onChange={(e) => set(i, { key: e.target.value })}
            aria-label="Field name"
          />
          <input
            className="field"
            value={row.value}
            placeholder="Value"
            maxLength={2000}
            onChange={(e) => set(i, { value: e.target.value })}
            aria-label={`Value of ${row.key || 'field'}`}
          />
          <IconButton label="Remove this field" tone="danger" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <TrashIcon />
          </IconButton>
        </div>
      ))}
      {dupes.size > 0 ? <p className="cf-problem">Each name can be used once.</p> : null}
      <div>
        <button type="button" className="btn btn-small" onClick={() => onChange([...rows, { key: '', value: '' }])}>
          + Add field
        </button>
      </div>
    </div>
  );
}
